/**
 * Docelowy URL po kliknięciu linku z maila (reset hasła).
 * Dodaj w Supabase → Authentication → URL configuration → Redirect URLs
 * (np. `https://app.forumkapitalu.pl/auth/nowe-haslo` oraz `http://localhost:3000/auth/nowe-haslo`).
 *
 * Starsza ścieżka `/auth/reset-password` nadal działa (route + przekierowanie tokenów).
 */
export const PASSWORD_RECOVERY_APP_PATH = '/auth/nowe-haslo';

const LEGACY_RESET_PATH = '/auth/reset-password';

/** Czy ścieżka to strona formularza nowego hasła (PKCE / implicit po redirect z Auth). */
export function isPasswordRecoveryFormPath(pathname: string): boolean {
  return (
    pathname === PASSWORD_RECOVERY_APP_PATH ||
    pathname.startsWith(`${PASSWORD_RECOVERY_APP_PATH}/`) ||
    pathname === LEGACY_RESET_PATH ||
    pathname.startsWith(`${LEGACY_RESET_PATH}/`)
  );
}

function readHashParams(hash: string): URLSearchParams | null {
  if (!hash || hash === '#') return null;
  return new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
}

/** Czy w URL są parametry sesji recovery z maila (fragment lub query). */
export function urlLooksLikePasswordRecoveryRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  const { hash, search } = window.location;
  const qs = new URLSearchParams(search);
  if (qs.get('type') === 'recovery' && qs.get('token_hash')) return true;
  if (qs.get('type') === 'recovery') return true;
  const hp = readHashParams(hash);
  if (hp?.get('type') === 'recovery') return true;
  if (hash.includes('type=recovery')) return true;
  return false;
}

/** Token z maila (nowy format — bez bezpośredniego linku Supabase /verify). */
export function readPasswordRecoveryTokenHash(): string | null {
  if (typeof window === 'undefined') return null;
  const qs = new URLSearchParams(window.location.search);
  if (qs.get('type') !== 'recovery') return null;
  const token = qs.get('token_hash')?.trim();
  return token || null;
}

export function clearPasswordRecoveryTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    window.history.replaceState({}, '', PASSWORD_RECOVERY_APP_PATH);
  } catch {
    /* ignore */
  }
}

/**
 * Link z Supabase często trafia na **Site URL** (np. `/`) z `#access_token=…&type=recovery…`.
 * Wtedy React ładuje złą gałąź (np. marketing na localhost) albo chroniony `/` panelu — zanim
 * `AuthProvider` zdąży przekierować, **hash ginie** przy `replace('/…')` bez fragmentu.
 * Wywołaj synchronicznie na początku `App` — pełne `location.replace` z tym samym `search`+`hash`.
 */
export function redirectPasswordRecoveryTokensToFormPath(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  if (isPasswordRecoveryFormPath(pathname)) return;
  if (!urlLooksLikePasswordRecoveryRedirect()) return;

  window.location.replace(`${window.location.origin}${PASSWORD_RECOVERY_APP_PATH}${search}${hash}`);
}
